import {
  createTRPCClient,
  httpSubscriptionLink,
  splitLink,
} from '@trpc/client';
import superjson from 'superjson';

import type { AppRouter } from '@infinex/main-service-contract/v2/client';

import { buildHttpBatchLink, buildHttpLink } from './buildHttpBatchLink';

import { DEFAULT_BATCH_PATHS } from '../constants';

export type GetExtraHeaders = () => Headers | Record<string, string>;

export interface CreateMainServiceClientOptions {
  /** The base URL of the main worker tRPC endpoint (e.g. `https://…/v2`). */
  url: string;
  /**
   * Optional separate base URL for SSE subscriptions. Defaults to {@link url}.
   * Under HTTP/1.1 browsers allow ~6 connections per origin; pointing
   * subscriptions at a distinct origin avoids starving queries. HTTP/2
   * multiplexes, so leaving this unset is correct in production.
   */
  subscriptionUrl?: string;
  /**
   * Optional callback returning additional request headers (e.g. auth tokens
   * for non-browser environments or testing).
   *
   * Note: native `EventSource` (used by subscriptions) cannot set custom
   * headers — browser cookie auth must use `withCredentials` (enabled).
   */
  getExtraHeaders?: GetExtraHeaders;
  /** Optional fetch override for cookie jars, tests, or non-browser runtimes. */
  fetch?: typeof fetch;
  /**
   * Procedures explicitly allowed to use request batching.
   * Defaults to {@link DEFAULT_BATCH_PATHS}.
   * Pass an array to fully override, or use the spread pattern to extend:
   * `[...DEFAULT_BATCH_PATHS, 'my.customProcedure']`
   */
  batchPaths?: readonly string[];
  /**
   * Maximum number of procedures in a single batch request for procedures in `batchPaths`.
   * @default 4
   */
  maxBatchSize?: number;
}

export type MainServiceTrpcClient = ReturnType<
  typeof createMainServiceTrpcClient
>;

/**
 * Create a tRPC client for the Infinex main worker / services/main v2 API.
 *
 * Subscriptions (`op.type === 'subscription'`) ride SSE via
 * `httpSubscriptionLink`. Queries/mutations keep the existing batch/non-batch
 * HTTP split.
 *
 * @example
 * ```ts
 * const trpcClient = createMainServiceTrpcClient({
 *   url: `${envVars.WORKER_MAIN_URL}/v2`,
 * });
 * ```
 */
export function createMainServiceTrpcClient({
  url,
  subscriptionUrl,
  getExtraHeaders,
  batchPaths = DEFAULT_BATCH_PATHS,
  maxBatchSize = 4,
  fetch,
}: CreateMainServiceClientOptions) {
  const batchPathsSet = new Set(batchPaths);
  return createTRPCClient<AppRouter>({
    links: [
      splitLink({
        condition: (op) => op.type === 'subscription',
        true: httpSubscriptionLink({
          url: subscriptionUrl ?? url,
          transformer: superjson,
          eventSourceOptions: () => ({ withCredentials: true }),
        }),
        false: splitLink({
          condition(op) {
            return batchPathsSet.has(op.path);
          },
          true: buildHttpBatchLink({
            url,
            maxItems: maxBatchSize,
            getExtraHeaders,
            fetch,
          }),
          false: buildHttpLink({ url, getExtraHeaders, fetch }),
        }),
      }),
    ],
  });
}
